import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { UserPlus, Sparkles, ArrowLeft, CheckCircle2, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('plan'); // 'pro' or null for free

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    
    try {
      const result = await signup(email, password);
      const token = result.access_token;
      
      // If user selected Pro plan, redirect to payment
      if (selectedPlan === 'pro' || selectedPlan === 'lifetime') {
        toast.success('Account created! Redirecting to payment...');
        
        // Create checkout session and redirect to Stripe
        const packageId = selectedPlan === 'lifetime' ? 'lifetime' : 'pro_monthly';
        try {
          const response = await axios.post(
            `${API}/payments/checkout`,
            {
              package_id: packageId,
              origin_url: window.location.origin
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          
          if (response.data.url) {
            window.location.assign(response.data.url);
            return;
          }
        } catch (paymentError) {
          console.error('Payment redirect failed:', paymentError);
          toast.error('Could not redirect to payment. You can upgrade later from the dashboard.');
          navigate('/app/setup');
        }
      } else {
        toast.success('Account created! Welcome aboard! 🎉');
        navigate('/app/setup');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error('Signup failed: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const freeFeatures = [
    '50 posts per session',
    'GA4 integration',
    '2 refreshes per day',
    'Delete 10 posts at once'
  ];

  const proFeatures = [
    '10,000 posts per session',
    'Unlimited refreshes',
    'Delete 500 posts at once',
    'Advanced sorting & filtering'
  ];

  const lifetimeFeatures = [
    'Everything in Pro',
    'One-time payment',
    'Lifetime updates',
    'No recurring fees'
  ];

  const features = selectedPlan === 'lifetime' ? lifetimeFeatures : selectedPlan === 'pro' ? proFeatures : freeFeatures;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-cyan-50 flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-purple-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Back to home button */}
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-slate-600 hover:text-purple-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <Card className="shadow-2xl shadow-purple-500/10 border-slate-200/60 backdrop-blur-sm bg-white/90" data-testid="signup-card">
          <CardHeader className="space-y-4 text-center pb-8">
            <div className="flex justify-center mb-2">
              <div className="bg-gradient-to-br from-purple-600 to-cyan-500 p-4 rounded-2xl shadow-lg shadow-purple-500/30">
                <UserPlus className="w-8 h-8 text-white" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-center gap-2 mb-2">
                {selectedPlan === 'lifetime' ? (
                  <Crown className="w-5 h-5 text-amber-500" />
                ) : selectedPlan === 'pro' ? (
                  <Crown className="w-5 h-5 text-purple-500" />
                ) : (
                  <Sparkles className="w-5 h-5 text-purple-500" />
                )}
                <span className="text-sm font-mono uppercase tracking-wider text-purple-600">WP Magica</span>
              </div>
              <CardTitle className="text-3xl font-heading font-bold tracking-tight bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
                {selectedPlan === 'lifetime' ? 'Create Lifetime Account' : selectedPlan === 'pro' ? 'Create Pro Account' : 'Create Account'}
              </CardTitle>
              <CardDescription className="text-base text-slate-600 mt-2">
                {selectedPlan === 'lifetime'
                  ? 'Sign up and complete payment for lifetime access'
                  : selectedPlan === 'pro' 
                  ? 'Sign up and complete payment to activate your Pro plan'
                  : 'Start cleaning up your WordPress content today'}
              </CardDescription>
              {selectedPlan === 'lifetime' && (
                <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-amber-100 to-yellow-100 px-4 py-2 rounded-full">
                  <Crown className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-700">Lifetime Deal - $99 one-time</span>
                </div>
              )}
              {selectedPlan === 'pro' && (
                <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-cyan-100 px-4 py-2 rounded-full">
                  <Crown className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-700">Professional Plan - $9.99/month</span>
                </div>
              )}
            </div>

            {/* Features list */}
            <div className="bg-gradient-to-br from-purple-50 to-cyan-50 rounded-xl p-4 mt-4">
              <p className="text-xs text-slate-600 font-semibold uppercase tracking-wider mb-3">What's Included:</p>
              <div className="grid grid-cols-2 gap-2">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className="h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="password-input"
                  className="h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500/20"
                />
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  Minimum 6 characters
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className="h-12 border-slate-200 focus:border-purple-500 focus:ring-purple-500/20"
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-6">
              <Button
                type="submit"
                className={`w-full h-12 font-semibold rounded-xl shadow-lg transition-all active:scale-95 ${
                  selectedPlan === 'lifetime' 
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black shadow-amber-500/30'
                    : 'bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white shadow-purple-500/30'
                }`}
                disabled={loading}
                data-testid="signup-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {(selectedPlan === 'pro' || selectedPlan === 'lifetime') ? 'Creating & redirecting to payment...' : 'Creating account...'}
                  </span>
                ) : (
                  selectedPlan === 'lifetime' ? 'Create Account & Pay $99' : selectedPlan === 'pro' ? 'Create Account & Pay' : 'Create Free Account'
                )}
              </Button>
              <p className="text-sm text-center text-slate-600">
                Already have an account?{' '}
                <Link 
                  to="/login" 
                  className="text-purple-600 hover:text-purple-700 font-semibold hover:underline" 
                  data-testid="login-link"
                >
                  Sign in
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
